import { Capacitor } from "@capacitor/core";
import { Directory, Filesystem } from "@capacitor/filesystem";
import { CAD_DOCUMENTS_EXTENSIONS } from "@clovis/server/src/app/config/pdftron/extensions";
import create from "zustand";
import { persist } from "zustand/middleware";
import { captureException } from "~/config/sentry";
import type { FileViewer_DataQuery } from "~/screens/App/components/FileViewerDialog/components/FileViewer/FileViewer.graphql";
import { base64ToBlob, blobToBase64 } from "./blobUtils";
import {
  deleteFromIndexedDB,
  getFromIndexedDB,
  hasInIndexedDB,
  saveToIndexedDB,
} from "./indexedDBStorage";
import { returnPWD } from "./utils";

type File = FileViewer_DataQuery;

interface OfflineFile {
  id: string;
  name: string;
  storedAt: string;
  path: string;
  mimeType: string;
  size: number;
  file: File;
}

interface OfflineFileVersion extends OfflineFile {
  fileVersionId: string;
}

interface OfflineFilesState {
  files: OfflineFile[];
  fileVersions: OfflineFileVersion[];
  isStoring: boolean;
  addFile: (file: OfflineFile) => Promise<void>;
  updateFile: (
    fileVersionId: string,
    location: { pageNumber: number; x: number; y: number },
    labels: unknown[],
    taskNumber: number
  ) => void;
  removeFile: (fileId: string) => Promise<void>;
  removeFileVersion: (fileId: string, fileVersionId: string) => Promise<void>;
  getStoredOfflineFile: (fileId: string) => OfflineFile | undefined;
  getStoredOfflineFileVersion: (
    fileId: string,
    fileVersionId: string
  ) => OfflineFileVersion | undefined;
  storeFileOffline: (file: File, tempUrl?: string) => Promise<void>;
  storeFileVersionOffline: (
    file: File,
    fileVersionId: string,
    tempUrl?: string,
    sourceFile?: Blob
  ) => Promise<void>;
  removeFiles: (fileIds: string[]) => Promise<void>;
  setStoring: (isStoring: boolean) => void;
  getStoredFile: (fileID: string) => Promise<Blob | undefined>;
  getStoredFileVersion: (
    fileID: string,
    fileVersionId: string
  ) => Promise<Blob | undefined>;
  hasStoredFile: (fileID: string) => Promise<boolean>;
  hasStoredFileVersion: (
    fileID: string,
    fileVersionId: string
  ) => Promise<boolean>;
  getFilesOfProject: (projectId: string) => OfflineFile[];
  getFilePWD: (fileId: string, folderNames: Map<string, string>) => string;
}

const isNative = Capacitor.isNativePlatform();

const getStoredBlob = async (file: OfflineFile): Promise<Blob | undefined> => {
  if (isNative) {
    const { data } = await Filesystem.readFile({
      directory: Directory.Data,
      path: file.path,
    });

    if (typeof data === "string") {
      return await base64ToBlob(data, file.mimeType);
    }
    if (data instanceof Blob) {
      return data;
    }
    return undefined;
  }

  return await getFromIndexedDB(file.path);
};

const hasStoredBlob = async (file: OfflineFile): Promise<boolean> => {
  if (isNative) {
    await Filesystem.stat({
      directory: Directory.Data,
      path: file.path,
    });
    return true;
  }

  return await hasInIndexedDB(file.path);
};

const getFilePath = (
  fileId: string,
  fileVersionId: string | undefined,
  fileName: string,
  isCadFile: boolean
): { directory: string; path: string } => {
  const directory = fileVersionId
    ? `offline-files/${fileId}/versions/${fileVersionId}`
    : `offline-files/${fileId}`;
  return {
    directory,
    path: isCadFile
      ? `${directory}/${fileName}.pdf`
      : `${directory}/${fileName}`,
  };
};

const persistOfflineFile = async (
  fileViewer: File,
  tempUrl?: string,
  fileVersionId?: string,
  sourceFile?: Blob
): Promise<OfflineFile> => {
  const { files_by_pk: file } = fileViewer;
  if (!file) {
    throw new Error("The document is no longer available");
  }

  const { id, name, url } = file;
  if (!url) {
    throw new Error("The document version has no download URL");
  }

  const fileExtension = name.split(".").pop()?.toLowerCase() ?? "";
  const isConvertedPdf =
    Boolean(tempUrl) || CAD_DOCUMENTS_EXTENSIONS.includes(fileExtension);
  let blob = !isConvertedPdf ? sourceFile : undefined;
  if (!blob) {
    const response = await fetch(tempUrl ?? url);
    if (!response.ok)
      throw new Error(`File download failed with ${response.status}`);
    blob = await response.blob();
  }
  if (tempUrl && blob.type !== "application/pdf")
    blob = new Blob([blob], { type: "application/pdf" });
  const { directory, path } = getFilePath(
    id,
    fileVersionId,
    name,
    isConvertedPdf
  );

  if (isNative) {
    const base64Data = await blobToBase64(blob);

    try {
      await Filesystem.readdir({
        directory: Directory.Data,
        path: directory,
      });
    } catch {
      await Filesystem.mkdir({
        directory: Directory.Data,
        path: directory,
        recursive: true,
      });
    }

    await Filesystem.writeFile({
      data: base64Data,
      directory: Directory.Data,
      path,
    });
  } else {
    await saveToIndexedDB(path, blob);
  }

  return {
    file: fileViewer,
    id,
    mimeType: blob.type,
    name,
    path,
    size: blob.size,
    storedAt: new Date().toISOString(),
  };
};

export const useOfflineFilesStore = create(
  persist<OfflineFilesState>(
    (set, get) => ({
      addFile: async (file) => {
        set((state) => ({
          files: state.files.filter((f) => f.id !== file.id).concat(file),
        }));
      },
      fileVersions: [],
      files: [],

      getFilePWD: (fileId, folderNames) => {
        const file = get().files.find((f) => f.id === fileId);
        if (!file) return "";
        return returnPWD(file, folderNames);
      },

      getFilesOfProject: (projectId) => {
        return get().files.filter(
          (f) => f.file?.files_by_pk?.project.id === projectId
        );
      },

      getStoredFile: async (fileID) => {
        const file = get().files.find((candidate) => candidate.id === fileID);
        if (!file) return;

        try {
          return await getStoredBlob(file);
        } catch (error) {
          console.error("Error retrieving offline file:", error);
          captureException(error, {
            extra: {
              action: "getStoredFile",
              fileId: fileID,
            },
          });
          return undefined;
        }
      },

      getStoredFileVersion: async (fileID, fileVersionId) => {
        const file = get().getStoredOfflineFileVersion(fileID, fileVersionId);
        if (!file) return;

        try {
          return await getStoredBlob(file);
        } catch (error) {
          console.error("Error retrieving offline file version:", error);
          captureException(error, {
            extra: {
              action: "getStoredFileVersion",
              fileId: fileID,
              fileVersionId,
            },
          });
          return undefined;
        }
      },

      getStoredOfflineFile: (fileId) => {
        return get().files.find((file) => file.id === fileId);
      },

      getStoredOfflineFileVersion: (fileId, fileVersionId) => {
        return get().fileVersions.find(
          (file) => file.id === fileId && file.fileVersionId === fileVersionId
        );
      },

      hasStoredFile: async (fileID) => {
        const file = get().getStoredOfflineFile(fileID);
        if (!file) {
          return false;
        }

        try {
          return await hasStoredBlob(file);
        } catch {
          return false;
        }
      },

      hasStoredFileVersion: async (fileID, fileVersionId) => {
        const file = get().getStoredOfflineFileVersion(fileID, fileVersionId);
        if (!file) {
          return false;
        }

        try {
          return await hasStoredBlob(file);
        } catch {
          return false;
        }
      },

      isStoring: false,

      removeFile: async (fileId) => {
        const storedFiles = get().files.filter((file) => file.id === fileId);
        if (storedFiles.length === 0) return;

        try {
          if (isNative) {
            for (const file of storedFiles) {
              await Filesystem.deleteFile({
                directory: Directory.Data,
                path: file.path,
              });
            }
          } else {
            const paths = new Set(storedFiles.map((file) => file.path));
            for (const path of paths) {
              await deleteFromIndexedDB(path);
            }
          }

          set((state) => ({
            files: state.files.filter((file) => file.id !== fileId),
          }));
        } catch (error) {
          captureException(error, {
            extra: {
              action: "removeFile",
              fileId,
            },
          });
        }
      },

      removeFileVersion: async (fileId, fileVersionId) => {
        const storedFile = get().getStoredOfflineFileVersion(
          fileId,
          fileVersionId
        );
        if (!storedFile) return;

        try {
          if (isNative) {
            await Filesystem.deleteFile({
              directory: Directory.Data,
              path: storedFile.path,
            });
          } else {
            await deleteFromIndexedDB(storedFile.path);
          }

          set((state) => ({
            fileVersions: state.fileVersions.filter(
              (file) =>
                file.id !== fileId || file.fileVersionId !== fileVersionId
            ),
          }));
        } catch (error) {
          captureException(error, {
            extra: {
              action: "removeFileVersion",
              fileId,
              fileVersionId,
            },
          });
        }
      },

      removeFiles: async (fileIds) => {
        for (const fileId of fileIds) {
          await get().removeFile(fileId);
        }
      },

      setStoring: (isStoring) => set({ isStoring }),

      storeFileOffline: async (fileViewer, tempUrl) => {
        set({ isStoring: true });
        try {
          const offlineFile = await persistOfflineFile(fileViewer, tempUrl);
          await get().addFile(offlineFile);
        } catch (error) {
          console.error("Error storing file offline:", error);
          captureException(error, {
            extra: {
              action: "storeFileOffline",
              fileId: fileViewer.files_by_pk?.id,
              fileName: fileViewer.files_by_pk?.name,
            },
          });
        } finally {
          set({ isStoring: false });
        }
      },

      storeFileVersionOffline: async (
        fileViewer,
        fileVersionId,
        tempUrl,
        sourceFile
      ) => {
        if (!fileVersionId) {
          throw new Error("A document version is required for offline storage");
        }

        set({ isStoring: true });
        try {
          const offlineFile = await persistOfflineFile(
            fileViewer,
            tempUrl,
            fileVersionId,
            sourceFile
          );
          const versionedFile: OfflineFileVersion = {
            ...offlineFile,
            fileVersionId,
          };

          set((state) => ({
            fileVersions: state.fileVersions
              .filter(
                (file) =>
                  file.id !== offlineFile.id ||
                  file.fileVersionId !== fileVersionId
              )
              .concat(versionedFile),
          }));
        } catch (error) {
          console.error("Error storing offline file version:", error);
          captureException(error, {
            extra: {
              action: "storeFileVersionOffline",
              fileId: fileViewer.files_by_pk?.id,
              fileName: fileViewer.files_by_pk?.name,
              fileVersionId,
            },
          });
          throw error;
        } finally {
          set({ isStoring: false });
        }
      },

      updateFile: async (fileVersionId, location, labels, taskNumber) => {
        const file = get().files;
        if (!file) return;

        const updatedFiles = file.map((f) => {
          return {
            ...f,
            file: {
              ...f.file,
              files_by_pk: {
                ...f.file.files_by_pk,
                id: f.file.files_by_pk?.id ?? "",
                versions: f.file.files_by_pk?.versions?.map((v) => {
                  if (v.id === fileVersionId) {
                    return {
                      ...v,
                      tasks: [
                        ...v.tasks,
                        {
                          location: {
                            page_number: location.pageNumber,
                            pdf_location: true,
                            x: location.x,
                            y: location.y,
                          },
                          task: {
                            is_validated_by: null,
                            labels: labels,
                            number: taskNumber,
                          },
                        },
                      ],
                    };
                  }
                  return v;
                }),
              },
            },
          };
        });

        set({ files: updatedFiles as OfflineFile[] });
      },
    }),
    {
      getStorage: () => ({
        getItem: (name) => {
          return localStorage.getItem(name);
        },
        removeItem: (name) => {
          localStorage.removeItem(name);
        },
        setItem: (name, value) => {
          localStorage.setItem(name, value);
        },
      }),
      name: "offline-files-storage",
      partialize: (state) => ({
        fileVersions: state.fileVersions,
        files: state.files,
      }),
    }
  )
);

export type { File, OfflineFile, OfflineFileVersion };
